import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  CircularProgress,
} from '@mui/material'
import PropTypes from 'prop-types';
import { sortReviews } from '../reviewsTable.helpers';
import { useIsMobileView } from '../../../utils/isMobileView'

import styles from './ReviewsList.module.scss'

const ReviewsList = ({ areLoading, items, openReviews }) => {
  const { t } = useTranslation()
  const isMobileView = useIsMobileView()

  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')

  const formatDate = (dateStr) => {
    if (!dateStr) return ''

    const date = new Date(dateStr)

    return date.toLocaleDateString('en-us', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    })
  }

  const getOpenReviewFromCampaign = (id) => (
    openReviews.filter((review) => review.campaignId === id).length
  )

    const handleSort = (property) => {
      const newOrder = sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc'
      setSortBy(property)
      setSortOrder(newOrder)
    }

    const sortedItems = useMemo(() => sortReviews({ reviews: items || [], sortBy, sortOrder }),
    [items, sortBy, sortOrder])

 // ======== DESKTOP ========
  const renderTableHeadDesktop = () => (
    sortedItems?.length > 0 && (
    <TableRow sx={{ '& th': { backgroundColor: '#F5F5F5', height: '10px' } }}>
      <TableCell className={styles.headerCellDesktopHash}>
        #
      </TableCell>
      <TableCell className={styles.headerCellDesktop}>
        <TableSortLabel
          className={styles.headerLabelDesktop}
          active={sortBy === 'name'}
          direction={sortOrder}
          onClick={() => handleSort('name')}
        >
          {t('label-certification-name')}
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.headerCellDesktop}>
        {t('label-pending-reviews')}
      </TableCell>
      <TableCell className={styles.headerCellDesktop}>
        <TableSortLabel
          className={styles.headerLabelDesktop}
          active={sortBy === 'endDate'}
          direction={sortOrder}
          onClick={() => handleSort('endDate')}
        >
          {t('label-due-date')}
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.headerCellDesktop}>
        {t('label-completion-status')}
      </TableCell>
    </TableRow>
     ))

  const renderTableBodyDesktop = () => sortedItems
        .map((review, index) => (
          <TableRow
            className={styles.tableBodyRowDesktop}
            key={review.id}
          >

            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {index + 1}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {review?.name}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {getOpenReviewFromCampaign(review?.id)}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {formatDate(review?.endDate)}
            </TableCell>
            <TableCell sx={{ fontSize: '16px' }} className={styles.tableCellDesktop}>
              {}
            </TableCell>

          </TableRow>
        ))

  const renderDesktop = () => (
    <div className={styles.containerDesktop}>
      {(areLoading || (items && items.length > 0)) && (
      <div className={styles.tableContainerback}>
        <h1 style={{ fontFamily: 'Johnson Text', fontSize: '16px', letterSpacing: '0.4px', paddingBottom: '35px', paddingLeft: '15px' }}>{t('label-pending-review')}</h1>
        <p className={styles.resultsDesktop}>
          {items?.length || 0} {t('results')}
        </p>
        <TableContainer className={styles.tableContainerDesktop}>
          <Table stickyHeader className={styles.tableDesktop}>
            <TableHead>{renderTableHeadDesktop()}</TableHead>
            <TableBody>
              {!areLoading ? (
                <TableRow>
                  <TableCell sx={{ border: 'none' }} colSpan={6}>
                    <div className={styles.loaderContainerDesktop}>
                      <div className={styles.loadingDesktop}>{t('loading')}</div>
                      <CircularProgress sx={{ color: 'black' }} />
                    </div>
                  </TableCell>
                </TableRow>
                ) : (
                  renderTableBodyDesktop()
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      )}
    </div>
  )

  // ======== MOBILE ========
  const renderReviewTableMobile = (review, index) => (
    <div
      key={review.id}
      className={styles.cardMobile}
    >

      <div className={styles.fieldBlockMobile}>
        <div className={styles.fieldLabelMobile}>#</div>
        <div className={styles.fieldValueMobile}>{index + 1}</div>
      </div>

      <div className={styles.fieldBlockMobile}>
        <div className={styles.fieldLabelMobile}>{t('label-certification-name')}</div>
        <div className={styles.fieldValueMobile}>{review?.name}</div>
      </div>

      <div className={styles.fieldBlockMobile}>
        <div className={styles.fieldLabelMobile}>{t('label-pending-reviews')}</div>
        <div className={styles.fieldValueMobile}>{getOpenReviewFromCampaign(review?.id)}</div>
      </div>

      <div className={styles.fieldBlockMobile}>
        <div className={styles.fieldLabelMobile}>{t('label-due-date')}</div>
        <div className={styles.fieldValueMobile}>{formatDate(review?.endDate)}</div>
      </div>

      <div className={styles.fieldBlockMobile}>
        <div className={styles.fieldLabelMobile}>{t('label-completion-status')}</div>
        <div className={styles.fieldValueMobile}>{}</div>
      </div>

    </div>
    )

  const renderMobile = () => (
    <div className={styles.containerMobile}>
      {(areLoading || (items && items.length > 0)) && (
        <div className={styles.listWrapperMobile}>
          <h1 style={{ fontFamily: 'Johnson Text', fontSize: '16px', letterSpacing: '0.4px', paddingLeft: '10px' }}>{t('label-pending-review')}</h1>
          <p className={styles.resultsMobile}>
            {items?.length || 0} {t('results')}
          </p>

          <div className={styles.cardListMobile}>
            { sortedItems.map((review, index) => (
                     renderReviewTableMobile(review, index)
                ))}
          </div>

        </div>
      )}
    </div>
  )

  return isMobileView ? renderMobile() : renderDesktop()
}
ReviewsList.propTypes = {
  areLoading: PropTypes.bool,
  items: PropTypes.array,
  openReviews: PropTypes.array,
}

ReviewsList.defaultProps = {
  areLoading: false,
  items: [],
  openReviews: [],
}

export default ReviewsList
